import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Fab,
  CircularProgress
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';

interface ImagePreviewProps {
  open: boolean;
  image: string | null;
  fileName?: string;
  onClose: () => void;
  title: string;
  handleDownloadPDF?: () => void;
  hasDownloadPDFButton?: boolean;
  isPdfGenerating?: boolean;
}

const base64ToBlob = (base64: string): Blob => {
  const [header, data] = base64.split(",", 2);

  const start = header.indexOf(":");
  const end = header.indexOf(";");
  const mime =
    start !== -1 && end !== -1
      ? header.substring(start + 1, end)
      : "image/png";

  const bstr = atob(data);
  const u8arr = new Uint8Array(bstr.length);

  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.codePointAt(i) ?? 0;
  }

  return new Blob([u8arr], { type: mime });
};

const ImagePreview: React.FC<ImagePreviewProps> = ({
  open,
  image,
  fileName = 'screenshot',
  onClose,
  title,
  handleDownloadPDF,
  hasDownloadPDFButton = false,
  isPdfGenerating = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleCopy = async () => {
    if (!image) return;

    setLoading(true);
    setSuccess(false);

    try {
      if (!navigator.clipboard || !globalThis.ClipboardItem) {
        throw new Error('Clipboard API not supported');
      }

      let blob: Blob;

      if (image.startsWith('data:')) {
        blob = base64ToBlob(image);
      } else {
        const response = await fetch(image);
        if (!response.ok) throw new Error('Image fetch failed');
        blob = await response.blob();
      }

      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2200);

    } catch (err) {
      console.error('Copy failed', err);

      // fallback → open image
      if (typeof image === 'string') {
        globalThis.open(image, '_blank');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!image) return;

    const link = document.createElement('a');
    link.href = image;
    link.download = `${fileName}.png`;
    link.click();

    onClose();
  };

  const buttonSx = {
    width: 40,
    height: 40,
    fontWeight: 600,
    textTransform: 'none',
  };

  const handleEmailClick = async () => {
    const subject = `${title}_${fileName}`;
    const pageUrl = globalThis.location.href;

    const body = `Dashboard link:\n${pageUrl}\n\n(Paste the screenshot with Ctrl+V)`;

    let copied = false;

    try {
      if (image && navigator.clipboard && globalThis.ClipboardItem) {

        let blob: Blob;

        // ✅ Detect base64 vs URL
        if (image.startsWith('data:')) {
          blob = base64ToBlob(image);
        } else {
          const response = await fetch(image);
          if (!response.ok) throw new Error('Image fetch failed');
          blob = await response.blob();
        }

        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob })
        ]);

        copied = true;
      }
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }

    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    globalThis.location.href = mailto;

    if (!copied) {
      alert('Could not copy screenshot automatically. Please copy it manually.');
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Header */}
      <Box sx={{ backgroundColor: "#003771", px: 2, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: "#FFFFFF !important" }} />
        </IconButton>
      </Box>

      {/* Image Preview */}
      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          backgroundColor: '#f5f5f5',
        }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt="Screenshot Preview"
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            }}
          />
        ) : (
          <Typography variant="body2">No preview available</Typography>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', background: 'white', px: 2, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Copy Button with Loader & Check Animation */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Fab
              color={success ? 'success' : 'primary'}
              onClick={handleCopy}
              disabled={loading}
              sx={{
                ...buttonSx,
              }}
            >
              <Tooltip title="Copy to Clipboard" arrow placement='top'>
                {success ? <CheckIcon /> : <ContentCopyIcon />}
              </Tooltip>
            </Fab>
          </Box>

          {/* Download Button */}
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={!image}
          >
            Download AS PNG
          </Button>

          {/* Download Button */}
          {hasDownloadPDFButton &&
            <Button
              variant="contained"
              onClick={handleDownloadPDF}
              disabled={!image || isPdfGenerating}
              startIcon={isPdfGenerating ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={isPdfGenerating ? {
                '&.Mui-disabled': {
                  color: 'white !important',
                  backgroundColor: 'primary.main',
                  opacity: 0.8,
                }
              } : undefined}
            >
              {isPdfGenerating ? "Downloading..." : "Download AS PDF"}
            </Button>
          }

          <Fab
            color={'primary'}
            onClick={handleEmailClick}
            sx={{
              ...buttonSx,
            }}
          >
            <Tooltip title="Mail To.." arrow placement='top'>
              <ForwardToInboxIcon />
            </Tooltip>
          </Fab>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePreview;